// What did people get told? — one Pacific day's briefs, every owner.
//
// The day picker is built from the days that actually have rows rather than by counting back
// from today, which is deliberate: `briefs.brief_on` is the *Pacific* day and Postgres'
// `current_date` on Render is UTC, so "today" is a question this panel refuses to ask. A
// missing morning shows up as a missing day in the list, which is the answer somebody wants.

import { useState } from 'react';
import type { AdminBriefRow, AdminBriefsPayload } from '../api/types';
import { formatCount } from '../lib/format';
import { Markdown } from './Markdown';
import { formatStamp } from './format.ts';

function BriefCard({ brief }: { brief: AdminBriefRow }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="briefcard">
      <div className="briefbar">
        <button type="button" className="brieftoggle" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show'}
        </button>
        <span className="acctname">{brief.owner_email}</span>
        <span className="briefwhen">written {formatStamp(brief.written_at)}</span>
      </div>
      <p className="sm">
        {formatCount(brief.pick_count)} new · {formatCount(brief.top_five_count)} top five ·{' '}
        {formatCount(brief.moved_count)} moved
        {brief.picks_waiting != null ? ` · ${formatCount(brief.picks_waiting)} waiting for approval` : ''}
      </p>
      {open ? <Markdown body={brief.body_markdown} /> : null}
    </article>
  );
}

export function BriefsPanel({
  payload,
  briefOn,
  onBriefOn,
}: {
  payload: AdminBriefsPayload | undefined;
  briefOn: string;
  onBriefOn: (value: string) => void;
}) {
  const briefs = payload?.briefs ?? [];
  const days = payload?.days ?? [];

  return (
    <>
      <div className="adminfilters">
        <label className="field">
          <span className="fieldlab">Day</span>
          <select value={briefOn} onChange={event => onBriefOn(event.target.value)}>
            <option value="">Newest recorded</option>
            {days.map(day => <option key={day} value={day}>{day}</option>)}
          </select>
        </label>
        <span className="admintrunc">
          Pacific days, from the rows themselves. `brief_on` is Pacific and Postgres'
          `current_date` is UTC, so this never asks for "today".
        </span>
      </div>
      {briefs.length === 0 ? (
        <p className="briefempty">
          No brief was written for {payload?.brief_on || 'any recorded day'}. `write_briefs`
          runs at 06:00 Pacific and writes one row per strategist plus the admin one-liner.
        </p>
      ) : (
        <div className="briefcards">
          {briefs.map(brief => <BriefCard key={brief.owner_email} brief={brief} />)}
        </div>
      )}
    </>
  );
}
